<script setup lang="ts">
import { LoadErrorAlert } from '@/shared/ui/LoadErrorAlert'
import { WidgetShell } from '@/shared/ui/WidgetShell'

/**
 * A widget of figures: `WidgetShell` with a title (and a button beside it), an optional
 * headline figure, `SummaryCardRow`s of label and value, and a note under them.
 *
 * Values arrive formatted — the card does not know money from counts. While `load-error` is
 * set the body is `LoadErrorAlert` instead, and `retry` is its "Try again".
 */
withDefaults(
  defineProps<{
    title: string
    /** The one figure the card is about, drawn large above the rows. */
    value?: string
    /** Body text right under `value`, saying what the figure is per — "monthly +1% of Ad revenue". */
    caption?: string
    loading?: boolean
    loadError?: string
    retrying?: boolean
  }>(),
  {
    value: undefined,
    caption: undefined,
    loading: false,
    loadError: undefined,
    retrying: false
  }
)

const emit = defineEmits<{
  (e: 'retry'): void
}>()

defineSlots<{
  actions?: () => unknown
  default?: () => unknown
  footer?: () => unknown
}>()
</script>

<template>
  <WidgetShell :title="title" :loading="loading" class="summary-card">
    <template v-if="$slots.actions && !loadError" #actions>
      <slot name="actions" />
    </template>

    <LoadErrorAlert
      v-if="loadError"
      :message="loadError"
      :retrying="retrying"
      @retry="emit('retry')"
    />
    <template v-else>
      <b
        v-if="value !== undefined"
        class="summary-card__value"
        :class="{ 'summary-card__value--captioned': caption }"
      >
        {{ value }}
      </b>
      <span v-if="caption" class="summary-card__caption">{{ caption }}</span>
      <ul v-if="$slots.default" class="summary-card__rows">
        <slot />
      </ul>
      <div v-if="$slots.footer" class="summary-card__footer">
        <slot name="footer" />
      </div>
    </template>
  </WidgetShell>
</template>

<style lang="scss" scoped>
.summary-card {
  height: 100%;
}

.summary-card__value {
  display: block;
  padding-bottom: 8px;
  font-size: 24px;
  line-height: 36px;
}

.summary-card__value--captioned {
  padding-bottom: 0;
}

.summary-card__caption {
  display: block;
  padding-bottom: 8px;
}

.summary-card__rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.summary-card__footer {
  padding-top: 12px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
</style>
